import React from "react";
import { Helmet } from "react-helmet";
import "./TermsServices.css";

const TermsServices = () => {
  const siteUrl = "https://www.samsoftix.com";
  const pageUrl = `${siteUrl}/terms-services`;

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: "SamSoftix",
        description:
          "SamSoftix is a software house and IT training center providing website development, mobile apps, ecommerce, AI, SEO, custom software and IT services.",
        inLanguage: "en-PK",
      },
      {
        "@type": ["LocalBusiness", "ProfessionalService"],
        "@id": `${siteUrl}/#business`,
        name: "SamSoftix",
        url: siteUrl,
        telephone: "+92-332-0472184",
        email: "info@samsoftix.com",
        description:
          "SamSoftix provides website development, mobile app development, ecommerce solutions, AI automation, SEO, custom software and IT services from Wan, Sambrial, Punjab, Pakistan, with remote services for clients internationally.",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Wan",
          addressRegion: "Punjab",
          addressCountry: "PK",
        },
        areaServed: [
          {
            "@type": "City",
            name: "Wan",
          },
          {
            "@type": "City",
            name: "Sambrial",
          },
          {
            "@type": "City",
            name: "Sialkot",
          },
          {
            "@type": "Country",
            name: "Pakistan",
          },
          {
            "@type": "Country",
            name: "United Arab Emirates",
          },
          {
            "@type": "Country",
            name: "Saudi Arabia",
          },
          {
            "@type": "Country",
            name: "United States",
          },
          {
            "@type": "Country",
            name: "United Kingdom",
          },
        ],
        sameAs: [
          "https://www.facebook.com/samsoftix",
          "https://www.linkedin.com/company/samsoftix",
          "https://www.instagram.com/samsoftix",
        ],
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Terms & Services | SamSoftix",
        description:
          "Read the SamSoftix Terms & Services covering service agreements, booking and cancellation, payments, delivery, client responsibilities, confidentiality, liability and governing law.",
        isPartOf: {
          "@id": `${siteUrl}/#website`,
        },
        about: {
          "@id": `${siteUrl}/#business`,
        },
        inLanguage: "en-PK",
        breadcrumb: {
          "@id": `${pageUrl}#breadcrumb`,
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: siteUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Terms & Services",
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <div className="terms-page">
      <Helmet>
        {/* Basic SEO */}
        <html lang="en-PK" />

        <title>
          Terms & Services | SamSoftix IT & Software Solutions Pakistan
        </title>

        <meta
          name="description"
          content="Read SamSoftix Terms & Services covering service agreements, booking and cancellation, payment terms, project delivery, client responsibilities, confidentiality, liability and governing law."
        />

        <meta
          name="keywords"
          content="SamSoftix Terms and Services, SamSoftix terms, IT service terms Pakistan, software development terms Pakistan, website development terms, mobile app development terms, software house terms, service booking terms, project payment terms, IT services Sambrial, software company Sialkot, SamSoftix Pakistan"
        />

        <meta name="author" content="SamSoftix" />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        <meta
          name="googlebot"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />

        {/* Canonical */}
        <link rel="canonical" href={pageUrl} />

        {/* Geographic SEO */}
        <meta name="geo.region" content="PK-PB" />
        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />

        <meta
          property="og:title"
          content="Terms & Services | SamSoftix IT & Software Solutions"
        />

        <meta
          property="og:description"
          content="Review SamSoftix service agreements, booking policies, payment terms, project delivery conditions, client responsibilities, confidentiality and other service terms."
        />

        <meta property="og:url" content={pageUrl} />

        <meta property="og:site_name" content="SamSoftix" />

        <meta property="og:locale" content="en_PK" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary" />

        <meta
          name="twitter:title"
          content="Terms & Services | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Read the terms and service policies for SamSoftix website development, software, mobile app, ecommerce, SEO and IT services."
        />

        {/* Browser / UI */}
        <meta name="theme-color" content="#06111f" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="terms-hero">
        <div className="terms-hero-inner">
          <span className="terms-kicker">
            SAMSOFTIX • SERVICE POLICIES
          </span>

          <h1>Terms & Services</h1>

          <p>
            Understand our policies, service terms, payment conditions,
            project requirements and client responsibilities before using
            SamSoftix services.
          </p>

          <div className="terms-location-tags">
            <span>Wan</span>
            <span>Sambrial</span>
            <span>Sialkot</span>
            <span>Pakistan</span>
            <span>Remote Worldwide</span>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="terms-intro">
        <div className="terms-container">
          <div className="terms-intro-card">
            <span className="terms-section-label">
              IMPORTANT INFORMATION
            </span>

            <h2>Please Review Before Booking a Service</h2>

            <p>
              These Terms & Services explain the general conditions that
              apply when you request or purchase services from SamSoftix.
              Project-specific requirements, pricing, timelines and other
              conditions may also be agreed separately between SamSoftix
              and the client.
            </p>

            <p>
              By submitting a service request or proceeding with a project,
              you acknowledge that you have reviewed and accepted the
              applicable terms.
            </p>
          </div>
        </div>
      </section>

      {/* Terms */}
      <main className="terms-content">
        <div className="terms-container">
          <article className="terms-card">
            <span className="terms-number">01</span>

            <div>
              <h2>Service Agreement</h2>

              <p>
                By booking services with SamSoftix, you agree to our terms
                and conditions. All projects and IT solutions are delivered
                according to the agreed scope and timeline.
              </p>

              <p>
                The final scope of work may depend on the requirements,
                features, technologies, resources and deliverables agreed
                between SamSoftix and the client.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">02</span>

            <div>
              <h2>Booking & Cancellation</h2>

              <p>
                Clients must provide accurate information during service
                booking. Cancellations or rescheduling should be communicated
                at least 24 hours before the scheduled service.
              </p>

              <p>
                Certain services may have non-refundable fees depending on
                the work already performed, resources reserved or terms
                agreed for the project.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">03</span>

            <div>
              <h2>Payment Terms</h2>

              <p>
                Payments can be made via bank transfer, online payment, or
                other agreed methods. For projects, partial upfront payment
                may be required before work begins.
              </p>

              <p>
                Full payment is required before final delivery unless
                otherwise agreed in writing between SamSoftix and the client.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">04</span>

            <div>
              <h2>Service Delivery</h2>

              <p>
                SamSoftix provides website development, software solutions,
                mobile applications, ecommerce solutions, SEO, digital
                services, technical support and other agreed IT services.
              </p>

              <p>
                Timelines may vary depending on project complexity, client
                input, approvals, requested revisions, third-party services,
                hosting providers, app stores or other external dependencies.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">05</span>

            <div>
              <h2>Client Responsibilities</h2>

              <p>
                Clients are responsible for providing the information,
                content, approvals and access required to complete the
                agreed service.
              </p>

              <ul>
                <li>
                  Provide accurate project details and requirements.
                </li>

                <li>
                  Respond to queries and approval requests in a timely manner.
                </li>

                <li>
                  Ensure proper access to systems or resources required for
                  remote support or development.
                </li>

                <li>
                  Maintain backups of important data before service execution.
                </li>

                <li>
                  Provide legally authorized content, images, documents,
                  accounts and other materials supplied for the project.
                </li>
              </ul>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">06</span>

            <div>
              <h2>Confidentiality</h2>

              <p>
                SamSoftix treats client information shared during projects
                and consultations as confidential and will not intentionally
                disclose sensitive client information to third parties
                without consent, except where disclosure is required by law
                or necessary to provide an agreed third-party service.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">07</span>

            <div>
              <h2>Limitation of Liability</h2>

              <p>
                While SamSoftix strives for high-quality delivery, SamSoftix
                is not liable for indirect losses, damages or delays caused
                by client-side issues or external factors beyond reasonable
                control.
              </p>

              <p>
                Such factors may include third-party hosting services,
                domain providers, payment providers, app stores, internet
                outages, external APIs or other dependencies outside
                SamSoftix's direct control.
              </p>
            </div>
          </article>

          <article className="terms-card">
            <span className="terms-number">08</span>

            <div>
              <h2>Governing Law</h2>

              <p>
                These terms and services are governed by the laws of
                Pakistan. Any disputes arising will be addressed in the
                applicable jurisdiction.
              </p>
            </div>
          </article>
        </div>
      </main>

      {/* Contact CTA */}
      <section className="terms-cta">
        <div className="terms-container">
          <div className="terms-cta-card">
            <span className="terms-section-label">
              HAVE QUESTIONS?
            </span>

            <h2>Need Clarification Before Booking?</h2>

            <p>
              If you have questions about our service terms, project
              requirements, payment conditions or delivery process, contact
              SamSoftix before starting your project.
            </p>

            <div className="terms-cta-actions">
              <a
                href="/serviceBooking"
                className="terms-primary-btn"
              >
                Book a Service
              </a>

              <a
                href="https://wa.me/923320472184"
                target="_blank"
                rel="noopener noreferrer"
                className="terms-whatsapp-btn"
              >
                WhatsApp SamSoftix
              </a>
            </div>

            <div className="terms-contact-line">
              <span>info@samsoftix.com</span>
              <span>+92 332 0472184</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsServices;